import './library-page.web.css';

import { createElement } from 'react';
import { ActivityIndicator, View } from 'react-native';

import {
  SidebarDatabaseIcon,
  SidebarPageBlankIcon,
} from '@/components/layout/SidebarIcons.web';
import { formatLibraryDate } from '@/lib/library/format-library-dates';
import type { LibraryFilter, LibraryRow } from '@/lib/library/types';

type LibraryTableProps = {
  rows: LibraryRow[];
  filter: LibraryFilter;
  memberNames: Map<string, string>;
  loading: boolean;
  restoringId: string | null;
  onOpenRow: (row: LibraryRow) => void;
  onRestore: (row: LibraryRow) => void;
};

function memberInitial(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    return '?';
  }
  return trimmed.charAt(0).toUpperCase();
}

function formatSource(row: LibraryRow): string {
  const area = row.source === 'family' ? 'Family' : 'Personal';
  if (row.parentTitle) {
    return `${area} · ${row.parentTitle}`;
  }
  return area;
}

function rowHref(row: LibraryRow): string {
  return row.type === 'database' ? `/database/${row.id}` : `/page/${row.id}`;
}

export function LibraryTable({
  rows,
  filter,
  memberNames,
  loading,
  restoringId,
  onOpenRow,
  onRestore,
}: LibraryTableProps) {
  if (loading) {
    return (
      <View className="mt-8 items-center">
        <ActivityIndicator />
      </View>
    );
  }

  const showLastVisited = filter === 'recents';
  const showRestore = filter === 'trash';

  return createElement(
    'div',
    { className: 'cs-library-wrap' },
    createElement(
      'table',
      { className: 'cs-library-table' },
      createElement(
        'thead',
        null,
        createElement(
          'tr',
          null,
          createElement('th', { className: 'cs-library-th', style: { width: '32%' } }, 'Page name'),
          createElement('th', { className: 'cs-library-th', style: { width: '16%' } }, 'Created by'),
          createElement('th', { className: 'cs-library-th', style: { width: '18%' } }, 'Source'),
          createElement('th', { className: 'cs-library-th', style: { width: '14%' } }, 'Last edited'),
          createElement(
            'th',
            { className: 'cs-library-th', style: { width: showRestore ? '12%' : '20%' } },
            'Last visited',
          ),
          showRestore
            ? createElement('th', { className: 'cs-library-th', style: { width: '8%' } }, '')
            : null,
        ),
      ),
      createElement(
        'tbody',
        null,
        rows.map((row) => {
          const creatorName = memberNames.get(row.createdBy) ?? 'Unknown';
          const lastEdited = formatLibraryDate(
            filter === 'trash' && row.deletedAt ? row.deletedAt : row.updatedAt,
          );
          const lastVisited = showLastVisited ? formatLibraryDate(row.visitedAt) : '—';

          return createElement(
            'tr',
            { key: row.id, className: 'cs-library-row' },
            createElement(
              'td',
              { className: 'cs-library-td' },
              createElement(
                'a',
                {
                  href: rowHref(row),
                  className: 'cs-library-name-link',
                  onClick: (event: MouseEvent) => {
                    event.preventDefault();
                    onOpenRow(row);
                  },
                },
                row.icon
                  ? createElement('span', { style: { fontSize: '1rem' } }, row.icon)
                  : row.type === 'database'
                    ? createElement(SidebarDatabaseIcon)
                    : createElement(SidebarPageBlankIcon),
                createElement('span', { className: 'truncate' }, row.title),
              ),
            ),
            createElement(
              'td',
              { className: 'cs-library-td' },
              createElement(
                'span',
                { style: { display: 'inline-flex', alignItems: 'center', gap: '0.375rem' } },
                createElement('span', { className: 'cs-library-avatar' }, memberInitial(creatorName)),
                creatorName,
              ),
            ),
            createElement('td', { className: 'cs-library-td' }, formatSource(row)),
            createElement('td', { className: 'cs-library-td' }, lastEdited),
            createElement('td', { className: 'cs-library-td' }, lastVisited),
            showRestore
              ? createElement(
                  'td',
                  { className: 'cs-library-td' },
                  createElement(
                    'button',
                    {
                      type: 'button',
                      className: 'cs-library-restore',
                      disabled: restoringId === row.id,
                      onClick: (event: MouseEvent) => {
                        event.stopPropagation();
                        onRestore(row);
                      },
                    },
                    restoringId === row.id ? 'Restoring…' : 'Restore',
                  ),
                )
              : null,
          );
        }),
      ),
    ),
  );
}
